import React, { SyntheticEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Input,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { GridColDef } from "@mui/x-data-grid";
import SearchBar from "../../components/SearchBar";
import TableComponent from "../../components/TableComponent";
import { isValidInput } from "../../utils/validations";
import useAuth from "../../hooks/useAuth";
import AddIcon from "@mui/icons-material/Add";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import {
  CommonBody,
  PaymentResponseType,
  Permission,
} from "../../types/General";

import {
  useExportPaymentsMutation,
  useGetAllPaymentsMutation,
  usePaymentStatusMutation,
} from "../../service/payments";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { errorToast } from "../../helpers";
import { PAYMENT_STATUS_LABELS, USER_ROLES } from "../../utils/enums";
import moment from "moment";
import { PriceFormatter } from "../../utils/commoFunctions";
import Chip from "../../components/Chip";
import { useIndividualUsersListMutation } from "../../service/users";
import { Uploadpdf } from "../../utils/mediaUpload";
import Loader from "../../constants/Loader";
import { toast } from "react-toastify";
import {
  useLazyGetInvoiceDetailsQuery,
  usePostSendInvoiceMutation,
} from "../../service/paymentModes";
import { useRejectPaymentMutation } from "../../service/paymentPlan";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const Payments = () => {
  const userData = useAuth();
  const [hidePermission, setHidePermission] = useState<Permission>();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [getInvoiceDetails] = useLazyGetInvoiceDetailsQuery();
  const [allPayments] = useGetAllPaymentsMutation();
  const [sendInvoiceById] = usePostSendInvoiceMutation();
  const [rejectApi] = useRejectPaymentMutation();
  const [paymentStatus] = usePaymentStatusMutation();
  const [exportPayments] = useExportPaymentsMutation();
  const [usersList] = useIndividualUsersListMutation();
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<PaymentResponseType[]>([]);
  const [page, setPage] = useState<number>(1);
  const [showAddSection, setShowAddSection] = useState(false);
  const [rejectModal, setShowRejectModal] = useState(false);

  const [attachedFile, setAttachedFile] = useState<string>("");
  const [rejectReason, setRejectReason] = useState<string>("");

  const [rawPdf, setRawPdf] = useState<any>(null);

  const [loadingUsers, setLoadingUsers] = useState(false);
  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const [valueTabs, setValueTabs] = useState(0);
  const handleChangeTabs = (event: SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
    setPage(1);
  };

  const [anchorEl1, setAnchorEl1] = React.useState<null | HTMLElement>(null);
  const open1 = Boolean(anchorEl1);
  const handleClick1 = (
    event: React.MouseEvent<HTMLButtonElement>,
    rowId: number
  ) => {
    setAnchorEl1(event.currentTarget);
    setSelectedId(rowId);
  };
  const handleClose1 = () => {
    setAnchorEl1(null);
    setSelectedId(null);
  };

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      flex: 1,
      minWidth: 200,
      field: "paymentDate",
      headerName: "Payment Date",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>
              {row?.created_at
                ? moment(row?.created_at).format("DD MMM YYYY hh:mm a")
                : "-"}
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 150,
      field: "applicationId",
      headerName: "Application Id",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{row?.application?.apk_id || "-"}</p>
          </div>
        );
      },
    },

    {
      flex: 1,
      minWidth: 150,
      field: "invoiceId",
      headerName: "Invoice Id",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div
            className="user_block"
            style={{ wordBreak: "break-word", whiteSpace: "normal" }}
          >
            <p>{row?.payment_id || "-"}</p>
          </div>
        );
      },
    },

    {
      flex: 1,
      minWidth: 300,
      field: "applicantDetails",
      headerName: "User Details",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <figure
              onClick={() => navigate(`/users/individual/${row?.user?.id}`)}
            >
              <img
                src={
                  row?.user?.profile_picture?.media_url ||
                  "/static/images/user_placeholder.png"
                }
                alt=""
              />
            </figure>
            <p>
              <b>{row?.user?.name || "-"}</b> {row?.user?.country_code}{" "}
              {row?.user?.phone_no || ""} <br />
              {row?.user?.email || "-"}
            </p>
          </div>
        );
      },
    },

    {
      flex: 1,
      minWidth: 150,
      field: "paymentMode",
      headerName: "Payment Mode",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{row?.plan_mode?.title || "-"}</p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 150,
      field: "paymentType",
      headerName: "Payment Type",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        const partNumber = row?.plan_part_no?.part_number;
        return (
          <div className="user_block">
            <p>
              {partNumber !== null && partNumber !== undefined
                ? PAYMENT_STATUS_LABELS[row?.plan_part_no?.part_number]
                : "-"}
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 150,
      field: "amountPaid",
      headerName: "Amount Paid",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>
              {PriceFormatter(row?.payment_amt_after_vat?.toString()) || "-"}
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 150,
      field: "vat",
      headerName: "VAT",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{PriceFormatter(row?.vat?.toString()) || "-"}</p>
          </div>
        );
      },
    },

    ...(valueTabs === 1
      ? [
          {
            flex: 1,
            minWidth: 150,
            field: "receipt",
            headerName: "Receipt",
            editable: false,
            sortable: false,
            renderCell: ({ row }: any) => {
              return (
                <>
                  {row?.payment_slip_partner?.length ? (
                    <Button
                      className="btn btn_blue"
                      onClick={() =>
                        window?.open(row?.payment_slip_partner?.[0]?.media_url)
                      }
                      style={{ height: "30px", padding: "5px 12px" }}
                    >
                      View Receipt
                    </Button>
                  ) : (
                    "-"
                  )}
                </>
              );
            },
          },
          {
            flex: 1,
            minWidth: 150,
            field: "paymentApproval",
            headerName: "Payment Approval",
            editable: false,
            sortable: false,
            renderCell: ({ row }: any) => {
              return (
                <>
                  {row?.is_rejected ? (
                    <Chip
                      label="Rejected"
                      color="#D32F2F"
                      backgroundColor="#FFEBEE"
                    />
                  ) : row?.is_verified ? (
                    <Chip
                      label={"Approved"}
                      color={"#4CAF50"}
                      backgroundColor={"#E8F5E9"}
                    />
                  ) : (
                    <Chip
                      label="Pending"
                      color="#B28900"
                      backgroundColor="#FFF8E1"
                    />
                  )}
                </>
              );
            },
          },
        ]
      : []),

    {
      flex: 0.1,
      minWidth: 80,
      field: "action",
      headerName: "Action",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        const isMenuOpen = open1 && selectedId === row.id;

        return (
          <div className="table_actions">
            <Box>
              <IconButton
                aria-controls={isMenuOpen ? "basic-menu1" : undefined}
                aria-haspopup="true"
                aria-expanded={isMenuOpen ? "true" : undefined}
                onClick={(event) => handleClick1(event, row?.id)}
              >
                <img src="/static/images/more_icon.svg" alt="" />
              </IconButton>

              {isMenuOpen && (
                <Menu
                  className="setting_menu"
                  id="basic-menu1"
                  anchorEl={anchorEl1}
                  open={isMenuOpen}
                  onClose={handleClose1}
                  MenuListProps={{
                    "aria-labelledby": "basic-button1",
                  }}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      overflow: "visible",
                      boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.05)",
                      borderRadius: "8px",
                      mt: 1,
                    },
                  }}
                  transformOrigin={{ horizontal: "right", vertical: "top" }}
                  anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                >
                  <MenuItem onClick={() => navigate(`view/${row?.id}`)}>
                    View
                  </MenuItem>
                  {row?.is_rejected != true ? (
                    <MenuItem
                      onClick={() => {
                        handleSendInvoice();

                        setSelectedRow(row);
                      }}
                    >
                      {row?.payment_slip_user?.[0]?.id
                        ? "Resend Invoice"
                        : "Send Invoice"}
                    </MenuItem>
                  ) : null}

                  {valueTabs === 1 &&
                    !row?.is_verified &&
                    !row?.is_rejected && (
                      <>
                        <MenuItem
                          onClick={() => {
                            setSelectedRow(row);
                            changePaymentStatus(row?.id);
                          }}
                        >
                          Approve
                        </MenuItem>

                        <MenuItem
                          onClick={() => {
                            setSelectedRow(row);
                            handleRejectInvoice();
                          }}
                        >
                          Reject Payment
                        </MenuItem>
                      </>
                    )}
                </Menu>
              )}
            </Box>
          </div>
        );
      },
    },
  ];

  const getPaymentList = async () => {
    setLoading(true);
    try {
      if (valueTabs === 2) {
        const body = {
          search: debouncedSearchTerm.trim(),
          start: page,
          length: 10,
        };
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response = await usersList(encryptedBody).unwrap();

        setLoading(false);
        if (response?.status === 200) {
          setCount(response?.data?.total_records);
          // setSelectedPage(response?.data?.total_records);
        } else {
          setRows([]);
        }
      } else {
        const body = {
          start: page,
          length: 130,
          search: debouncedSearchTerm.trim(),
          type: valueTabs === 0 ? 2 : 1,
        };
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response = await allPayments(encryptedBody).unwrap();

        setLoading(false);
        if (response?.status === 200) {
          setRows(response?.data?.response_object || []);
          setCount(response?.data?.total_records);
        } else {
          setRows([]);
        }
      }
    } catch (error: any) {
      errorToast(error?.data[0]?.message || "Something went wrong");
      setLoading(false);
    }
  };

  const changePaymentStatus = async (id: number) => {
    setLoading(true);
    try {
      const body = {
        is_verified: true,
      };

      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await paymentStatus({
        body: encryptedBody,
        id,
      }).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        getPaymentList();
        handleClose1();
        toast.success("Payment approved successfully");
      } else {
        setRows([]);
      }
    } catch (error: any) {
      errorToast(error?.data[0]?.message || "");
      setLoading(false);
    }
  };
  const handleExportCSV = async () => {
    setLoading(true);
    try {
      const body = {
        search: debouncedSearchTerm.trim(),
        type: valueTabs === 0 ? 2 : 1,
      };

      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await exportPayments(encryptedBody).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        window.open(response?.file_urls, "_blank");
      }
    } catch (error: any) {
      errorToast(error?.data[0]?.message || "");
      setLoading(false);
    }
  };
  const handleSendInvoice = async () => {
    setLoading(true);
    setShowAddSection((prev) => !prev);
  };
  const handleRejectInvoice = async () => {
    setLoading(true);
    setShowRejectModal((prev) => !prev);
  };
  const checkPermission = () => {
    const permission = userData?.role_permission;
    if (permission?.length) {
      const foundPermission = permission?.find(
        (ele: Permission) => ele?.module === "Manage Payments"
      );

      if (foundPermission) {
        setHidePermission(foundPermission);
      } else {
        navigate(-1);
      }
    }
  };

  useEffect(() => {
    if (userData?.user_role_type === USER_ROLES.SUBADMIN) {
      checkPermission();
    }
  }, [userData]);

  useEffect(() => {
    if (typeof hidePermission !== "undefined") {
      if (!hidePermission?.can_view) {
        navigate("/permission-denied", { replace: true });
      }
    }
  }, [hidePermission, userData]);
  useEffect(() => {
    getPaymentList();
  }, [debouncedSearchTerm, page, valueTabs]);

  const fetchUsers = async (searchTerm = "") => {
    try {
      const body = generateEncryptedKeyBody({
        search: searchTerm.trim(),
        start: 1,
        length: 120,
      });
      setLoading(true);

      const response = await usersList(body).unwrap();

      if (response?.status === 200) {
        setLoading(false);
      }
    } catch (error) {
      toast.error(error?.data[0]?.message || "");
    }
    setLoading(false);
  };
  // const handleUpload = async () => {
  //   if (!attachedFile) {
  //     toast.error("Please upload a PDF");
  //     return;
  //   }

  //   try {
  //     const formData = new FormData();
  //     formData.append("media", rawPdf);
  //     setLoadingUsers(true);
  //     const response = await sendInvoiceById({
  //       body: formData,
  //       id: selectedId,
  //     }).unwrap();

  //     if (response?.status === 200) {
  //       toast.success(response?.message || "");
  //       setLoading(false);
  //       setShowAddSection(false);

  //       setAttachedFile("");
  //       getPaymentList();
  //       setLoadingUsers(false);
  //     }
  //   } catch (error: any) {
  //     errorToast(error?.data?.message || "");
  //     setLoadingUsers(false);
  //   }
  // };
  const handleUpload = async () => {
    if (!attachedFile) {
      toast.error("Please upload a PDF");
      return;
    }

    try {
      setLoadingUsers(true);

      // :repeat: Optional: Check if this is a resend

      const formData = new FormData();
      formData.append("media", rawPdf);
      const invoiceResponse = await getInvoiceDetails({
        body: formData,
        id: selectedId,
      }).unwrap();
      const isResend = invoiceResponse?.data?.payment_slip_user?.length > 0;

      const response = await sendInvoiceById({
        body: formData,
        id: selectedId,
      }).unwrap();

      if (response?.status === 200) {
        toast.success(
          isResend ? "Invoice resent successfully" : "Invoice sent successfully"
        );
        setLoading(false);
        setShowAddSection(false);
        setAttachedFile("");
        getPaymentList();
      }
    } catch (error: any) {
      errorToast(error?.data[0]?.message || "Something went wrong");
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleOtherDocumentsUpload = async (event: any) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validType = "application/pdf";

    if (file.type !== validType) {
      errorToast("Only PDF files are allowed");
      event.target.value = null;

      return;
    }

    setLoadingUsers(true);

    try {
      setRawPdf(file);
      const uploadResponse = await Uploadpdf(file);

      const mediaUrl = uploadResponse?.data?.[0]?.media_url;
      const mediaId = uploadResponse?.data?.[0]?.id;

      if (!mediaUrl || !mediaId) {
        errorToast("Upload failed. Please try again.");
        return;
      }

      setAttachedFile(mediaUrl);
      // setRawPdf(file);

      event.target.value = null;
    } catch (error) {
      errorToast(
        error?.data[0]?.message || "Something went wrong during file upload."
      );
      event.target.value = null;
    } finally {
      setLoadingUsers(false);
      event.target.value = null;
    }
  };
  const handleRejectApiCall = async () => {
    setLoadingUsers(true);
    try {
      const payload = {
        is_rejected: true,
        reason: rejectReason.trim(),
      };

      const encryptedBody = generateEncryptedKeyBody(payload);
      const response = await rejectApi({
        body: encryptedBody,
        id: selectedId,
      }).unwrap();
      if (response?.status === 200) {
        setLoadingUsers(false);

        toast.success(response?.message || "Rejection submitted successfully.");
        setShowRejectModal(false);
        setRejectReason("");
        getPaymentList();
      }
    } catch (error) {
      errorToast(
        error?.data[0]?.message || "Something went wrong while rejecting."
      );
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (valueTabs === 2) fetchUsers();
  }, [valueTabs]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1>Payments</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> - Manage
          Payments - Payments
        </p>
      </div>
      <Loader loading={loading || loadingUsers} />

      <div className="cards">
        <Box className="custom_tabs" sx={{ width: "100%" }}>
          <div className="table_header">
            <div className="left_s">
              <Box className="search_box">
                <SearchBar
                  searchTerm={searchTerm}
                  placeholder="Search"
                  setDebouncedSearchTerm={setDebouncedSearchTerm}
                  value={searchTerm}
                  onCross={() => setSearchTerm("")}
                  onChange={(val: any) => {
                    if (isValidInput(val.target.value)) {
                      setSearchTerm(val.target.value);
                    }
                  }}
                />
              </Box>
            </div>
            <div className="right_s">
              <Box>
                {valueTabs === 2 && (
                  <Button
                    className="btn btn_highlight"
                    onClick={() => setShowAddSection((prev) => !prev)}
                    style={{ marginRight: 15 }}
                  >
                    Add Invoice
                  </Button>
                )}

                <Button
                  className="btn btn_highlight"
                  onClick={handleExportCSV}
                  disabled={!rows?.length}
                >
                  <img src="/static/images/upload_icon.svg" alt="Icon" /> Export
                  CSV
                </Button>
              </Box>
            </div>
          </div>

          <Tabs
            value={valueTabs}
            onChange={handleChangeTabs}
            aria-label="basic tabs example"
            className="custom_tabs_links"
          >
            <Tab label={`Online Payments`} {...a11yProps(0)} />
            <Tab label={`Offline Payments`} {...a11yProps(1)} />
            {/* <Tab label={`Invoice`} {...a11yProps(2)} /> */}
          </Tabs>
          <CustomTabPanel value={valueTabs} index={0}>
            <div className="sorting_table">
              <TableComponent
                columns={columns}
                rows={rows}
                count={count}
                page={page}
                onPageChange={handlePageChange}
                loading={loading}
              />
            </div>
          </CustomTabPanel>
          <CustomTabPanel value={valueTabs} index={1}>
            <div className="sorting_table">
              <TableComponent
                columns={columns}
                rows={rows}
                count={count}
                page={page}
                onPageChange={handlePageChange}
                loading={loading}
              />
            </div>
          </CustomTabPanel>
          <CustomTabPanel value={valueTabs} index={2}>
            <div className="sorting_table">
              <TableComponent
                columns={columns}
                rows={rows}
                count={count}
                page={page}
                onPageChange={handlePageChange}
                loading={loading}
              />
            </div>
          </CustomTabPanel>
        </Box>
      </div>
      <Dialog
        open={showAddSection}
        onClose={() => setShowAddSection(false)}
        maxWidth="sm"
        fullWidth
      >
        {/* <Loader loading={loadingUsers} /> */}
        <DialogTitle>Please Upload Invoice PDF</DialogTitle>
        <DialogContent>
          <Box sx={{ marginTop: 2 }}>
            <div className="upload_box_doc" style={{ width: "100%" }}>
              {loadingUsers ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <CircularProgress />
                </div>
              ) : (
                <label>
                  {attachedFile ? (
                    <>
                      <IconButton
                        className="crossIcon"
                        onClick={(e) => {
                          e.preventDefault();
                          setAttachedFile(null);
                        }}
                      >
                        <CloseIcon />
                      </IconButton>

                      {attachedFile.endsWith(".pdf") ? (
                        <div
                          className="pdf-preview"
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "10px",
                            height: "100%",
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            window.open(attachedFile, "_blank");
                          }}
                        >
                          <img
                            src="/static/images/pdf.png"
                            alt="PDF"
                            style={{ width: "60px", height: "60px" }}
                          />
                          <p style={{ fontSize: "12px", marginTop: "5px" }}>
                            View PDF
                          </p>
                        </div>
                      ) : (
                        <figure
                          onClick={() => window.open(attachedFile, "_blank")}
                        >
                          <img
                            src={attachedFile}
                            alt="Upload"
                            style={{ width: "100%", height: "100%" }}
                          />
                        </figure>
                      )}
                    </>
                  ) : null}

                  <Input
                    sx={{ display: "none" }}
                    type="file"
                    inputProps={{
                      accept: "application/pdf",
                    }}
                    onChange={(e) => handleOtherDocumentsUpload(e)}
                  />
                </label>
              )}
              {!attachedFile && !loadingUsers ? (
                <AddIcon className="add_icon" />
              ) : null}
            </div>
          </Box>
        </DialogContent>
        {!loadingUsers && attachedFile ? (
          <DialogActions>
            <Button
              onClick={() => {
                setShowAddSection(false);
                setAttachedFile("");
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpload}>
              {selectedRow?.payment_slip_user?.[0]?.id
                ? "Resend Invoice"
                : "Send Invoice"}
            </Button>
          </DialogActions>
        ) : null}
      </Dialog>
      <Dialog
        open={rejectModal}
        onClose={() => setShowRejectModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Please provide a reason for rejection</DialogTitle>
        <DialogContent>
          <Box sx={{ marginTop: 2 }}>
            <TextField
              multiline
              minRows={4}
              maxRows={10}
              fullWidth
              label="Reason for rejection"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={`${rejectReason.trim().length} characters (Min: 30, Max: 1000)`}
              error={
                rejectReason.trim().length < 30 ||
                rejectReason.trim().length > 1000
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#1a59cb",
                  },
                  "&:hover fieldset": {
                    borderColor: "#1a59cb",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1a59cb",
                  },
                },
                "& label": {
                  backgroundColor: "#fff",
                  paddingRight: "4px",
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          {!loadingUsers ? (
            <Button
              onClick={() => {
                setShowRejectModal(false);
                setRejectReason("");
              }}
            >
              Cancel
            </Button>
          ) : null}

          <Button
            style={{ marginRight: 10 }}
            onClick={handleRejectApiCall}
            disabled={
              loadingUsers ||
              rejectReason.trim().length < 30 ||
              rejectReason.trim().length > 1000
            }
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Payments;